<template>
  <!-- 采购合同台账（路桥定制）：按合同编号汇总，支持 excel 导入、勾选打印二维码 -->
  <div class="purchase-contract-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="请输入采购合同编号" @keydown.enter="resetSearch" clearable>
            <template #prefix>合同编号</template>
          </el-input>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="warning" @click="showImport = true">导入excel</page-button>
          <!-- 连续打印：勾选多个合同，按 list 逐条渲染后一次性送打印 -->
          <print-button template-key="LUQIAO_PURCHASE_CONTRACT" print-type="continuous" button-text="打印二维码"
                        :find-print-data="getPrintData"/>
        </template>
      </search-condition>

      <list-table>
        <!-- 通用导入组件：上传文件 / 下载模板，导入结果由本页弹框确认后再提交 -->
        <import-component ref="importComponent" v-model="showImport" :import-by-excel="handleImport"
                          @export-temp="downloadTemplate"/>
        <yq-table :data="dataList" height="100%" stripe border page-name="purchaseContractList"
                  @selectionChange="selectionChange">
          <yq-table-column type="selection" prop="selection" width="60rem" align="center" fixed="left"/>
          <yq-table-column label="合同编号" prop="contractNumber" min-width="220rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="物资清单" prop="productCount" width="140rem" align="center">
            <template v-slot="{row}">{{row.productCount || 0}} 个商品</template>
          </yq-table-column>
          <yq-table-column label="总数量" prop="totalQty" width="140rem" align="right">
            <template v-slot="{row}">{{formatQty(row.totalQty)}}</template>
          </yq-table-column>
          <!-- 入库/出库：已入库数量 / 已出库数量（对应 od_stock_order_item.bankup_7 = 合同号 的累计） -->
          <yq-table-column label="入库/出库" prop="inStockQty" width="180rem" align="center">
            <template v-slot="{row}">
              <span class="stock-in">{{formatQty(row.inStockQty)}}</span>
              <span class="stock-sep">/</span>
              <span class="stock-out">{{formatQty(row.outStockQty)}}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="二维码" prop="qrcodeImage" width="120rem" align="center">
            <template v-slot="{row}">
              <image-show v-model="row.qrcodeImage" width="0.8rem" height="0.8rem"/>
            </template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" width="100rem" align="center" fixed="right" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="showDetail(row)">明细</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 导入结果确认：商品已在后台更新，这里只确认台账明细怎么保存 -->
    <el-dialog v-model="showResult" title="导入数据确认" width="1200px" :close-on-click-modal="false"
               append-to-body @closed="importRows = []">
      <div class="import-tip">
        本次解析到 <b>{{importRows.length}}</b> 条物资明细，商品信息已按 excel 更新/新增；
        台账明细要点击「提交」才会保存，点击「取消」不保存台账明细。
      </div>
      <yq-table :data="importRows" height="420px" border stripe>
        <yq-table-column type="index" prop="index" label="序号" width="70rem" align="center"/>
        <yq-table-column label="物资类别" prop="categoryName" min-width="140rem" :show-overflow-tooltip="true"/>
        <yq-table-column label="物资名称" prop="productName" min-width="160rem" :show-overflow-tooltip="true"/>
        <yq-table-column label="规格型号" prop="spec" min-width="160rem" :show-overflow-tooltip="true"/>
        <yq-table-column label="计量单位" prop="unitName" width="100rem" align="center"/>
        <yq-table-column label="材质" prop="material" min-width="140rem" :show-overflow-tooltip="true"/>
        <yq-table-column label="数量" prop="qty" width="90rem" align="right">
          <template v-slot="{row}">{{formatQty(row.qty)}}</template>
        </yq-table-column>
        <yq-table-column label="单价" prop="price" width="110rem" align="right">
          <template v-slot="{row}">{{formatMoney(row.price)}}</template>
        </yq-table-column>
        <yq-table-column label="金额" prop="amount" width="120rem" align="right">
          <template v-slot="{row}">{{formatMoney(row.amount)}}</template>
        </yq-table-column>
        <yq-table-column label="采购合同号" prop="contractNumber" min-width="200rem" :show-overflow-tooltip="true"/>
        <yq-table-column label="商品" prop="newProduct" width="90rem" align="center">
          <template v-slot="{row}">
            <el-tag size="small" :type="row.newProduct === 1 ? 'success' : 'info'">
              {{row.newProduct === 1 ? '新增' : '更新'}}
            </el-tag>
          </template>
        </yq-table-column>
      </yq-table>
      <div class="import-footer">
        <div class="update-type">
          <span class="label">更新方式</span>
          <el-radio-group v-model="updateType">
            <el-radio :value="1">覆盖更新</el-radio>
            <el-radio :value="2">追加更新</el-radio>
          </el-radio-group>
          <span class="hint">
            {{updateType === 1 ? '删除相同合同编号的台账数据后重新插入' : '按商品匹配：已有则更新，没有则新增，不删除数据'}}
          </span>
        </div>
        <div>
          <page-button @click="showResult = false">取消</page-button>
          <page-button type="write" el-type="success" :disabled="submitting" @click="submitData">提交</page-button>
        </div>
      </div>
    </el-dialog>

    <!-- 合同明细：查看每个商品的出入库情况 -->
    <el-dialog v-model="showDetailDialog" :title="'合同明细 · ' + (detailContract.contractNumber || '')" width="1200px"
               append-to-body>
      <div v-loading="detailLoading">
        <div class="detail-summary">
          <span>物资清单：<b>{{detailContract.productCount || 0}}</b> 个商品</span>
          <span>总数量：<b>{{formatQty(detailContract.totalQty)}}</b></span>
          <span>入库/出库：<b class="stock-in">{{formatQty(detailContract.inStockQty)}}</b>
            <span class="stock-sep">/</span>
            <b class="stock-out">{{formatQty(detailContract.outStockQty)}}</b>
          </span>
        </div>
        <yq-table :data="detailRows" height="420px" border stripe empty-text="该合同下没有物资明细">
          <yq-table-column type="index" prop="index" label="序号" width="70rem" align="center"/>
          <yq-table-column label="物资类别" prop="categoryId" min-width="140rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">{{ categoryName(row.categoryId) }}</template>
          </yq-table-column>
          <yq-table-column label="物资名称" prop="productName" min-width="160rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="规格型号" prop="spec" min-width="160rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="计量单位" prop="unitId" width="100rem" align="center">
            <template v-slot="{row}">{{ unitName(row.unitId) }}</template>
          </yq-table-column>
          <yq-table-column label="材质" prop="material" min-width="140rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="数量" prop="qty" width="90rem" align="right">
            <template v-slot="{row}">{{ formatQty(row.qty) }}</template>
          </yq-table-column>
          <yq-table-column label="单价" prop="price" width="110rem" align="right">
            <template v-slot="{row}">{{ formatMoney(row.price) }}</template>
          </yq-table-column>
          <yq-table-column label="金额" prop="amount" width="120rem" align="right">
            <template v-slot="{row}">{{ formatMoney(row.amount) }}</template>
          </yq-table-column>
          <yq-table-column label="采购合同号" prop="contractNumber" min-width="200rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="已入库" prop="inStockQty" width="90rem" align="right">
            <template v-slot="{row}">{{ formatQty(row.inStockQty) }}</template>
          </yq-table-column>
          <yq-table-column label="已出库" prop="outStockQty" width="90rem" align="right">
            <template v-slot="{row}">{{ formatQty(row.outStockQty) }}</template>
          </yq-table-column>
        </yq-table>
      </div>
      <template #footer>
        <page-button @click="showDetailDialog = false">关闭</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import ImportComponent from "@/components/h5/ImportComponent.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import PrintButton from "@/views/h5/common/print/template/print-button.vue";
import purchaseContractApi from "@/api/dz-luqiao/purchaseContractApi";
import commonApi from "@/api/commonApi";
import excelUtil from "@/util/excelUtil";
import {useRoute, useRouter} from "vue-router";
import ImageShow from "@/components/h5/image/ImageShow.vue";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "PurchaseContractList",
  components: {
    ImageShow,
    PrintButton, ImportComponent, TableButton,
    YqTable, YqTableColumn, PageButton, SearchCondition, FormPage, ListTable, ListPage
  },
  data() {
    return {
      searchInfo: {pageNumber: 1, pageSize: 10},
      dataList: [],
      selectedRows: [],
      showImport: false,
      // 导入结果确认弹框
      showResult: false,
      importRows: [],
      // 1 覆盖更新，2 追加更新（默认追加，避免误删）
      updateType: 2,
      submitting: false,
      // 合同明细弹框
      showDetailDialog: false,
      detailLoading: false,
      detailContract: {},
      detailRows: [],
      // 明细里把分类/单位编码翻译成名称
      categoryList: [],
      unitList: [],
    };
  },
  created() {
    this.resetSearch();
    // 物资分类是商户自己的字典（商品管理分类树同源），计量单位是唯一性字典
    commonApi.queryDictionaryList("PRODUCT_CATEGORY").then(list => this.categoryList = list || []);
    commonApi.queryUniqueDictionaryList("PRODUCT_UNIT").then(list => this.unitList = list || []);
  },
  methods: {
    categoryName(categoryId) {
      if (!categoryId) {
        return "—";
      }
      const hit = this.categoryList.find(c => String(c.dictCode) === String(categoryId));
      return hit ? hit.dictName : categoryId;
    },
    unitName(unitId) {
      if (!unitId) {
        return "—";
      }
      const hit = this.unitList.find(u => String(u.dictCode) === String(unitId));
      return hit ? hit.dictName : unitId;
    },
    /** 查看合同明细：每个商品的台账数据与出入库数量 */
    async showDetail(row) {
      this.detailContract = row || {};
      this.detailRows = [];
      this.showDetailDialog = true;
      this.detailLoading = true;
      try {
        this.detailRows = await purchaseContractApi.queryItemList(row.contractNumber) || [];
      } finally {
        this.detailLoading = false;
      }
    },
    async queryList() {
      const {records, totalCount} = await purchaseContractApi.queryContractList(this.searchInfo);
      this.dataList = records || [];
      this.searchInfo.totalCount = totalCount || 0;
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    selectionChange(list) {
      this.selectedRows = list || [];
    },
    /** 下载导入模板（后台接口返回 xlsx 文件流） */
    downloadTemplate() {
      excelUtil.exportExcel("/luqiao/contract/purchase/exportTemp", {}, "物资采购合同申请物资清单.xlsx");
    },
    /**
     * 通用导入组件的上传回调
     *
     * 后台只同步商品并返回台账明细，这里弹框让用户确认；Promise resolve 后导入组件会关闭上传弹框。
     */
    handleImport(formData) {
      return purchaseContractApi.importExcel(formData).then(rows => {
        this.importRows = rows || [];
        this.updateType = 2;
        this.showResult = true;
        // 清掉通用组件里选中的文件，下次打开不要还挂着上一次的文件
        if (this.$refs.importComponent) {
          this.$refs.importComponent.fileList = [];
          this.$refs.importComponent.file = "";
        }
      });
    },
    /** 保存台账明细 */
    async submitData() {
      if (!this.importRows.length) {
        this.$message.error("没有可保存的台账明细");
        return;
      }
      this.submitting = true;
      try {
        await purchaseContractApi.submit({
          updateType: this.updateType,
          items: this.importRows.map(row => ({
            productId: row.productId,
            qty: row.qty,
            price: row.price,
            amount: row.amount,
            contractNumber: row.contractNumber,
            inStockQty: row.inStockQty,
            outStockQty: row.outStockQty,
          })),
        });
        this.$message.success("保存成功");
        this.showResult = false;
        this.resetSearch();
      } finally {
        this.submitting = false;
      }
    },
    /**
     * 打印数据（print-button 的 find-print-data 入口）
     *
     * 勾选多少条合同就打印多少张二维码，二维码内容约定为 contractNumber=合同编号。
     */
    async getPrintData() {
      if (!this.selectedRows.length) {
        return null;
      }
      const rows = [];
      for (const row of this.selectedRows) {
        // 二维码直接取列表查询带出来的（lq_purchase_contract_info.wx_qr_code），不再每次调微信；
        // 老合同没有二维码时兜底生成一次，后端会落库，下次打印就快了
        let qrcodeImage = row.qrcodeImage || "";
        if (!qrcodeImage) {
          qrcodeImage = await purchaseContractApi.ensureQrCode(row.contractNumber);
        }
        rows.push({
          contractNumber: row.contractNumber,
          // 旧的文本二维码保留，兼容没改过的打印模板；新模板请用「二维码(自定义)」绑定 qrcodeImage
          qrcode: "contractNumber=" + row.contractNumber,
          qrcodeImage: qrcodeImage || "",
          productCount: row.productCount || 0,
          totalQty: this.formatQty(row.totalQty),
          inStockQty: this.formatQty(row.inStockQty),
          outStockQty: this.formatQty(row.outStockQty),
        });
      }
      return rows;
    },
    formatQty(value) {
      if (value === null || value === undefined || value === "") {
        return "0";
      }
      return String(Number(value));
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2);
    },
  }
};
</script>

<style lang="scss" scoped>
.purchase-contract-list {
  height: 100%;
}

// 入库/出库颜色。弹框是 append-to-body 挂到 body 下的，不能嵌在 .purchase-contract-list 里
.stock-in {
  color: #67c23a;
}

.stock-sep {
  margin: 0 6px;
  color: #c0c4cc;
}

.stock-out {
  color: #e6a23c;
}

.detail-summary {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
  background: #f5f7fa;
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.import-tip {
  font-size: 13px;
  color: #606266;
  background: var(--el-color-primary-light-9);
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.import-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;

  .update-type {
    display: flex;
    align-items: center;
    gap: 8px;

    .label {
      font-size: 13px;
      color: #303133;
      font-weight: 600;
    }

    .hint {
      font-size: 12px;
      color: #909399;
    }
  }
}
</style>
